import IntroWizardsNavBar from '@/components/intro-wizards/intro-wizards-nav-bar/intro-wizard-nav-bar';
import WizardProgressIndicator from '@/components/intro-wizards/intro-wizards-step/components/wizard-progress-indicator/wizard-progress-indicator';
import useIsMobile from '@/hooks/use-is-mobile';
import { Box, Button, Divider, Flex, IconArrowRight, Text } from '@cmpsr/components';

type IntroWizardCoverProps = {
  onNextButtonClick: () => void;
  onBackButtonClick: () => void;
};

export default function IntroWizardStep05(props: IntroWizardCoverProps) {
  const isMobile = useIsMobile();

  return (
    <Box minH="100svh" background="gradient.dark" display="flex" flexDirection="column">
      <IntroWizardsNavBar onBackButtonClick={props.onBackButtonClick} showLogo={!isMobile} />
      <Flex
        flex="1"
        alignItems={{ base: 'flex-start', lg: 'center' }}
        justifyContent="center"
        maxW="500px"
        mt={{ base: 0, lg: '-80px' }}
        mx="auto"
        px={5}
      >
        <Flex flexDirection="column" color="text-light" gap={5}>
          <Text as="h1" variant="text-header-L" color="text-light">
            Before you start your Initial Risk Assessment, here are a few tips to{' '}
            <Text as="span" textStyle="text-header-l-v" color="text-light !important">
              keep in mind
            </Text>
            :
          </Text>

          <Divider borderColor="med-blue" />
          <Flex as="ol" flexDirection="column" gap={3} width="calc(100% - 20px)" alignSelf="flex-end">
            <li>
              <Text variant={{ base: 'text-body-meta-regular', lg: 'text-body-regular' }} color="text-light">
                Answer the questions as{' '}
                <Text as="span" fontWeight="600" color="text-light" fontStyle="italic">
                  honestly
                </Text>{' '}
                as you can. On the lifestyle questions, share what you currently do, not what you intend or try to do.
              </Text>
            </li>
            <Divider borderColor="med-blue" />
            <li>
              <Text variant={{ base: 'text-body-meta-regular', lg: 'text-body-regular' }} color="text-light">
                When answering questions, think about the{' '}
                <Text as="span" fontWeight="600" color="text-light" fontStyle="italic">
                  last few months
                </Text>{' '}
                as a time period, as opposed to the last few days or weeks.
              </Text>
            </li>
            <Divider borderColor="med-blue" />
            <li>
              <Text variant={{ base: 'text-body-meta-regular', lg: 'text-body-regular' }} color="text-light">
                You can use the{' '}
                <Text as="span" fontWeight="600" color="text-light" fontStyle="italic">
                  tooltips
                </Text>{' '}
                on each question to get more detailed instructions (e.g., how much is “1 serving”?)
              </Text>
            </li>
            <Divider borderColor="med-blue" />
            <li>
              <Text variant={{ base: 'text-body-meta-regular', lg: 'text-body-regular' }} color="text-light">
                If you don’t know the answer to a question, don’t worry; you can always{' '}
                <Text as="span" fontWeight="600" color="text-light" fontStyle="italic">
                  go back
                </Text>{' '}
                and answer it at another time.
              </Text>
            </li>
          </Flex>
          <Divider borderColor="med-blue" />
          <Text variant={{ base: 'text-body-meta-regular', lg: 'text-body-regular' }} color="text-light">
            Remember, this is your{' '}
            <Text as="span" fontWeight="600" color="text-light" fontStyle="italic">
              initial
            </Text>{' '}
            assessment. As you make changes your cancer risk analysis will automatically update.
          </Text>
          <Flex role="group" alignItems="center" justifyContent="space-between" w="full" aria-label="Slide controls">
            <Flex gap={2}>
              {Array.from({ length: 5 }).map((_, index) => (
                <WizardProgressIndicator key={index} active={index === 4} />
              ))}
            </Flex>
            <Button
              variant="accent"
              size="l"
              trailingIcon={<IconArrowRight />}
              onClick={props.onNextButtonClick}
              fontSize="16px"
            >
              Get Started
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}
